import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

const API_BASE = "https://api.vyapakprint.com/api";

const OrderDetail = () => {
  const { orderId } = useParams();

  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [notice, setNotice] = useState({ type: "", message: "" }); // success | error | info

  // productId -> product object
  const [productsById, setProductsById] = useState({});

  const getErrorMessage = (err, fallback = "Something went wrong.") =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  const showNotice = (type, message) => setNotice({ type, message });

  const money = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0";
    return n.toLocaleString("en-IN");
  };

  const statusColor = (s) => {
    const v = (s || "").toLowerCase();
    if (v === "delivered")
      return "bg-green-100 text-green-700 border-green-200";
    if (v === "shipped") return "bg-blue-100 text-blue-700 border-blue-200";
    if (v === "processing")
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    if (v === "cancelled" || v === "canceled")
      return "bg-red-100 text-red-700 border-red-200";
    return "bg-gray-100 text-gray-700 border-gray-200";
  };

  useEffect(() => {
    let alive = true;

    const fetchOrderAndProducts = async () => {
      setLoading(true);
      setError("");
      setNotice({ type: "", message: "" });
      setProductsById({});

      try {
        // ✅ Order
        const res = await axios.get(`${API_BASE}/orders/${orderId}`);
        if (!alive) return;

        const o = res?.data?.order || res?.data;
        setOrder(o);
        setStatus(o?.status || "");

        // ✅ Unique productIds from cart
        const ids = (o?.cartItems || [])
          .map((x) => x?.productId)
          .filter(Boolean);

        const uniqueIds = Array.from(new Set(ids));

        if (uniqueIds.length > 0) {
          const results = await Promise.allSettled(
            uniqueIds.map((id) =>
              axios.get(`${API_BASE}/products/getProductById/${id}`),
            ),
          );

          if (!alive) return;

          const map = {};
          results.forEach((r, idx) => {
            const id = uniqueIds[idx];
            if (r.status === "fulfilled") {
              const data = r.value?.data;
              // ✅ your API might return { product: {...} } or direct product
              map[id] = data?.product || data;
            } else {
              map[id] = null;
            }
          });

          setProductsById(map);
        }
      } catch (err) {
        if (!alive) return;
        setError(getErrorMessage(err, "Error fetching order details."));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    fetchOrderAndProducts();

    return () => {
      alive = false;
    };
  }, [orderId]);

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    setNotice({ type: "", message: "" });

    try {
      await axios.put(`${API_BASE}/orders/${orderId}/status`, {
        status: newStatus,
      });
      setStatus(newStatus);
      showNotice("success", `Order status updated to "${newStatus}".`);
    } catch (err) {
      showNotice(
        "error",
        getErrorMessage(err, "Failed to update order status."),
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const totalItems = useMemo(() => {
    return (order?.cartItems || []).reduce(
      (sum, it) => sum + Number(it?.quantity || 0),
      0,
    );
  }, [order]);

  const NoticeBox = () => {
    if (!notice.message) return null;
    const base = "px-4 py-3 rounded-lg text-sm border";
    const styles =
      notice.type === "success"
        ? "bg-green-50 border-green-200 text-green-800"
        : notice.type === "error"
          ? "bg-red-50 border-red-200 text-red-800"
          : "bg-blue-50 border-blue-200 text-blue-800";
    return <div className={`${base} ${styles}`}>{notice.message}</div>;
  };

  if (loading)
    return (
      <div className="text-center py-10 text-gray-600">Loading order...</div>
    );
  if (error)
    return <div className="text-center py-10 text-red-600">{error}</div>;
  if (!order)
    return (
      <div className="text-center py-10 text-gray-600">Order not found.</div>
    );

  const user = order?.userDetails || {};

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <NoticeBox />

        {/* Summary */}
        <div className="bg-white rounded-2xl shadow p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Order #{order?._id}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Placed on{" "}
                <span className="font-medium text-gray-700">
                  {order?.orderDate
                    ? new Date(order.orderDate).toLocaleString()
                    : "-"}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-3">
              <span
                className={`px-3 py-1 rounded-full border text-sm font-semibold ${statusColor(status)}`}
              >
                {status || "Unknown"}
              </span>

              <div className="text-right">
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-xl font-bold text-gray-800">
                  ₹{money(order?.totalPrice)}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <Stat label="User ID" value={order?.userId} />
            <Stat label="Items" value={String(totalItems)} />
            <Stat label="Voucher" value={order?.voucherCode || "-"} />
            <Stat label="Payment" value={order?.paymentMethod || "-"} />
          </div>
        </div>

        {/* Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Customer Details
              </h2>

              <div className="space-y-3 text-sm">
                <Row label="Name" value={user?.name} />
                <Row label="Business" value={user?.businessName} />
                <Row label="Mobile" value={user?.mobileNo} />
                <Row label="Email" value={user?.email} />
                <Row label="Address" value={user?.postalAddress} />
                <Row
                  label="Website"
                  value={
                    user?.webAddress ? (
                      <a
                        href={user.webAddress}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        {user.webAddress}
                      </a>
                    ) : (
                      "-"
                    )
                  }
                />
                <Row label="Other" value={user?.otherDetails} />
              </div>
            </div>

            {/* Status buttons */}
            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Update Status
              </h2>

              <div className="grid grid-cols-2 gap-3">
                <StatusBtn
                  disabled={updatingStatus}
                  active={status === "Processing"}
                  color="bg-yellow-500 hover:bg-yellow-600"
                  onClick={() => handleStatusChange("Processing")}
                  label={updatingStatus ? "Updating..." : "Processing"}
                />
                <StatusBtn
                  disabled={updatingStatus}
                  active={status === "Shipped"}
                  color="bg-blue-500 hover:bg-blue-600"
                  onClick={() => handleStatusChange("Shipped")}
                  label={updatingStatus ? "Updating..." : "Shipped"}
                />
                <StatusBtn
                  disabled={updatingStatus}
                  active={status === "Delivered"}
                  color="bg-green-500 hover:bg-green-600"
                  onClick={() => handleStatusChange("Delivered")}
                  label={updatingStatus ? "Updating..." : "Delivered"}
                />
                <StatusBtn
                  disabled={updatingStatus}
                  active={status === "Cancelled"}
                  color="bg-red-500 hover:bg-red-600"
                  onClick={() => handleStatusChange("Cancelled")}
                  label={updatingStatus ? "Updating..." : "Cancelled"}
                />
              </div>
            </div>
          </div>

          {/* Right */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Products</h2>

              <div className="space-y-4">
                {(order?.cartItems || []).map((item) => {
                  const product = productsById?.[item?.productId];

                  return (
                    <div
                      key={item?._id}
                      className="border rounded-2xl p-4 bg-gray-50"
                    >
                      <div className="flex gap-4">
                        {/* Product image */}
                        <div className="w-24 h-24 rounded-xl overflow-hidden border bg-white flex items-center justify-center">
                          {product?.images?.[0] ? (
                            <img
                              src={product.images[0]}
                              alt={product?.name || "Product"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs text-gray-400 px-2 text-center">
                              No image
                            </span>
                          )}
                        </div>

                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                            <div>
                              <h3 className="text-base font-bold text-gray-800">
                                {product?.name ||
                                  "Product details not available"}
                              </h3>

                              <p className="text-sm text-gray-600 mt-1">
                                Category:{" "}
                                <span className="font-medium">
                                  {product?.category?.name ||
                                    product?.category ||
                                    "-"}
                                </span>
                              </p>

                              <p className="text-xs text-gray-500 mt-1 break-all">
                                Product ID: {item?.productId}
                              </p>

                              {product?.description && (
                                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                  {product.description}
                                </p>
                              )}
                            </div>

                            <div className="text-right">
                              <p className="text-sm text-gray-700">
                                Qty:{" "}
                                <span className="font-semibold">
                                  {item?.quantity}
                                </span>
                              </p>
                              <p className="text-sm text-gray-700">
                                Price/piece:{" "}
                                <span className="font-semibold">
                                  ₹{money(item?.price)}
                                </span>
                              </p>
                              <p className="text-sm font-bold text-gray-800 mt-1">
                                Line Total: ₹
                                {money(
                                  Number(item?.price || 0) *
                                    Number(item?.quantity || 0),
                                )}
                              </p>
                            </div>
                          </div>

                          {/* Customizations */}
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            <Chip label="Die Shape" value={item?.dieShape} />
                            <Chip label="Paper Type" value={item?.paperType} />
                            <Chip
                              label="Window Cutting"
                              value={item?.windowCutting}
                            />
                            <Chip
                              label="Flap Opening"
                              value={item?.flapOpening}
                            />
                            <Chip label="Printing" value={item?.printing} />
                            <Chip
                              label="Lamination"
                              value={item?.laminationType}
                            />
                          </div>

                          {/* If you store uploaded artwork url */}
                          {item?.fileUrl && (
                            <div className="mt-3">
                              <a
                                href={item.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline text-sm"
                              >
                                View Uploaded File
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Payment details block */}
            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Payment Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <Stat
                  label="Total Price"
                  value={`₹${money(order?.totalPrice)}`}
                />
                <Stat label="Voucher Code" value={order?.voucherCode || "-"} />
                <Stat
                  label="Order Date"
                  value={
                    order?.orderDate
                      ? new Date(order.orderDate).toLocaleString()
                      : "-"
                  }
                />
                <Stat label="Status" value={status || "-"} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Helpers */
const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4">
    <p className="text-gray-500">{label}</p>
    <div className="text-gray-800 font-medium text-right break-words max-w-[60%]">
      {value || "-"}
    </div>
  </div>
);

const Chip = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-2 bg-white border rounded-lg px-3 py-2">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-gray-800">{value}</span>
    </div>
  );
};

const StatusBtn = ({ label, onClick, color, disabled, active }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-xl text-white transition font-semibold ${
      active ? "ring-2 ring-offset-2 ring-gray-300" : ""
    } ${disabled ? "bg-gray-400 cursor-not-allowed" : color}`}
  >
    {label}
  </button>
);

const Stat = ({ label, value }) => (
  <div className="bg-gray-50 rounded-xl p-4 border">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-800 break-all">
      {value || "-"}
    </p>
  </div>
);

export default OrderDetail;
